import { api, ApiError, Card, formatDate, Page } from '@kanos/web-core';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CalendarX2, Lock } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { useMe } from '../me';

const contractKey = (tenantId: string) => ['firm', tenantId, 'contract'] as const;

/**
 * Where the firm's contract stands (ADR-045), for every page - asked again after a minute, as the
 * API looks at it again after a minute: an end set, moved or taken back shows that soon.
 */
function useFirmContract() {
  const me = useMe();
  return useQuery({
    queryKey: contractKey(me.activeTenantId),
    queryFn: async () => (await api.GET('/api/v1/firm/contract')).data ?? null,
    staleTime: 60_000,
  }).data;
}

/** The API's refusals because of the contract's end - the app's picture of it is behind. */
const CONTRACT_REFUSALS = new Set(['identity.contract-ended', 'identity.firm-locked']);

/**
 * Asks where the firm's contract stands again as soon as the API refuses anything because of its
 * end: the window opened, or the firm was locked, since the app last asked. Until stopped.
 */
export function watchContractRefusals(queryClient: QueryClient, tenantId: string): () => void {
  const refused = (error: unknown) => {
    if (error instanceof ApiError && CONTRACT_REFUSALS.has(error.code)) {
      void queryClient.invalidateQueries({ queryKey: contractKey(tenantId) });
    }
  };
  const mutations = queryClient.getMutationCache().subscribe((event) => {
    if (event.type === 'updated' && event.action.type === 'error') refused(event.action.error);
  });
  const queries = queryClient.getQueryCache().subscribe((event) => {
    if (event.type === 'updated' && event.action.type === 'error') refused(event.action.error);
  });
  return () => {
    mutations();
    queries();
  };
}

/**
 * Why writing is closed (ADR-045): once the contract ended, the firm reads and exports until the
 * window closes - every button that changes something says so and stays disabled.
 */
export function useContractWriteLock(): string | null {
  const queryClient = useQueryClient();
  const me = useMe();
  useEffect(
    () => watchContractRefusals(queryClient, me.activeTenantId),
    [queryClient, me.activeTenantId],
  );
  return writeLockOf(useFirmContract());
}

/** The lock of a contract as it stands: only in the export window, with until when. */
export function writeLockOf(
  c: { phase: string; endsOn?: string | null; exportUntil?: string | null } | null | undefined,
): string | null {
  if (c?.phase !== 'export-window') return null;
  const until = c.exportUntil ?? c.endsOn;
  return `Der Vertrag mit Kanos ist beendet${
    until ? ` – bis ${formatDate(until)} können Sie nur noch lesen und exportieren` : ''
  }. Ändern lässt sich nichts mehr.`;
}

/**
 * The end of the firm's contract with Kanos (ADR-045), under the bar of every page once it is
 * set: until when everything goes on, and until when the firm can still read and export - with
 * the way to the export. Decoration of a kind: if the question fails, nothing shows.
 */
export function ContractBanner() {
  const c = useFirmContract();
  if (!c?.endsOn || (c.phase !== 'active' && c.phase !== 'export-window')) return null;
  return (
    <div
      role="status"
      aria-label="Vertragsende"
      className="flex shrink-0 flex-wrap items-start gap-x-2 gap-y-1 bg-warning-bg px-safe-4 py-2 text-sm text-warning-fg sm:px-safe-6 lg:px-safe-8"
    >
      <CalendarX2 aria-hidden className="mt-0.5 size-4 shrink-0" />
      <p className="min-w-0 flex-1">
        {c.phase === 'active' ? (
          <>
            <span className="font-semibold">
              Der Vertrag mit Kanos endet am {formatDate(c.endsOn)}.
            </span>{' '}
            Danach können Sie bis {formatDate(c.exportUntil ?? c.endsOn)} nur noch lesen und
            exportieren.
          </>
        ) : (
          <>
            <span className="font-semibold">Der Vertrag mit Kanos ist beendet.</span> Bis{' '}
            {formatDate(c.exportUntil ?? c.endsOn)} können Sie nur noch lesen und exportieren,
            danach wird die Kanzlei gesperrt und gelöscht.
          </>
        )}{' '}
        Die GwG-Aufzeichnungen bewahren Sie fünf Jahre selbst auf – sie sind im Export enthalten.
      </p>
      <Link
        to="/settings/$area"
        params={{ area: 'data-export' }}
        className="inline-flex min-h-11 items-center font-semibold underline underline-offset-2"
      >
        Daten exportieren
      </Link>
    </div>
  );
}

/**
 * A firm locked after its contract's end (ADR-045): instead of its pages, why - and the way back,
 * through the Kanos team. Every request of it is refused (`identity.firm-locked`).
 */
export function ContractGate({ children }: { children: ReactNode }) {
  const c = useFirmContract();
  if (c?.phase !== 'locked' && c?.phase !== 'erasure-due') return children;
  return (
    <Page title="Kanzlei gesperrt">
      <Card
        id="firm-locked"
        title="Der Vertrag mit Kanos ist beendet"
        description="Das Exportfenster ist vorbei; die Kanzlei ist gesperrt und wird danach gelöscht, sobald das Kanos-Team zustimmt."
      >
        <p className="flex items-start gap-2 text-sm text-muted">
          <Lock aria-hidden className="mt-0.5 size-4 shrink-0" />
          {c.lockedUntil
            ? `Gesperrt bis ${formatDate(c.lockedUntil)}. Für eine Rückkehr wenden Sie sich an das Kanos-Team.`
            : 'Für eine Rückkehr wenden Sie sich an das Kanos-Team.'}
        </p>
      </Card>
    </Page>
  );
}
